import { motion } from 'framer-motion';
import { ArrowLeftRight, Calendar, Timer, Hash, CheckCircle, Circle, Zap, ArrowUp, Battery } from 'lucide-react';
import { useProposalData } from '../../context';
import { Section, SectionHeader } from '../layout';
import { ImageGallery } from '../common';
import { ConditionBadge } from '../common/Badge';
import { formatCurrency, formatNumber } from '../../utils';

export function TradeInSection() {
  const proposal = useProposalData();

  if (!proposal.sections.showTradeIn || proposal.tradeIns.length === 0) {
    return null;
  }

  const totalTradeIn = proposal.tradeIns.reduce((sum, item) => sum + item.value, 0);

  return (
    <Section id="trade-in" className="bg-gray-50">
      <SectionHeader
        title="TRADE-IN EQUIPMENT"
        subtitle="Your Equipment"
      />

      {/* Summary Banner */}
      <motion.div
        className="mb-12 p-6 bg-gradient-to-r from-green-600 to-green-700 rounded-xl text-white"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
      >
        <div className="flex flex-wrap items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
              <ArrowLeftRight className="w-7 h-7" />
            </div>
            <div>
              <div className="text-green-100 text-sm uppercase tracking-wider">
                Total Trade-In Credit
              </div>
              <div className="font-display text-3xl tracking-wide">
                {proposal.tradeIns.length} Unit{proposal.tradeIns.length !== 1 ? 's' : ''} Accepted
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-green-100 text-sm mb-1">Credit Applied</div>
            <div className="font-mono text-4xl font-bold">
              -{formatCurrency(totalTradeIn)}
            </div>
          </div>
        </div>
      </motion.div>

      {/* Trade-In Cards */}
      <div className="grid md:grid-cols-2 gap-8">
        {proposal.tradeIns.map((tradeIn, index) => (
          <motion.div
            key={tradeIn.lineNumber}
            className="bg-white rounded-xl shadow-lg overflow-hidden"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.1 }}
          >
            {/* Card Header */}
            <div className="bg-hul-navy px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="w-8 h-8 bg-white/20 rounded flex items-center justify-center font-mono text-white text-sm">
                  #{tradeIn.lineNumber}
                </span>
                <span className="font-heading text-white uppercase tracking-wider">
                  Trade-In
                </span>
              </div>
              {tradeIn.condition && <ConditionBadge condition={tradeIn.condition} />}
            </div>

            <div className="p-6">
              <div className="grid sm:grid-cols-2 gap-6">
                {/* Image */}
                <div>
                  <ImageGallery
                    images={tradeIn.images}
                    alt={tradeIn.description}
                    videoUrl={tradeIn.videoUrl}
                  />
                </div>

                {/* Details */}
                <div>
                  {/* Title */}
                  <h3 className="font-display text-xl text-hul-navy tracking-wide mb-2">
                    {tradeIn.manufacturer && tradeIn.model
                      ? `${tradeIn.manufacturer} ${tradeIn.model}`
                      : tradeIn.description}
                  </h3>

                  {tradeIn.manufacturer && tradeIn.model && (
                    <p className="text-gray-600 text-sm mb-4">{tradeIn.description}</p>
                  )}

                  {/* Specs */}
                  <div className="space-y-3 mb-6">
                    {tradeIn.serial && (
                      <div className="flex items-center gap-3 text-sm">
                        <Hash className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-500">Serial:</span>
                        <span className="font-mono text-hul-navy">{tradeIn.serial}</span>
                      </div>
                    )}
                    {tradeIn.year && (
                      <div className="flex items-center gap-3 text-sm">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-500">Year:</span>
                        <span className="font-medium text-hul-navy">{tradeIn.year}</span>
                      </div>
                    )}
                    {tradeIn.hours !== undefined && (
                      <div className="flex items-center gap-3 text-sm">
                        <Timer className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-500">Hours:</span>
                        <span className="font-medium text-hul-navy">{formatNumber(tradeIn.hours)}</span>
                      </div>
                    )}
                    {/* Extended specs - only show key ones if available */}
                    {tradeIn.tireType && (
                      <div className="flex items-center gap-3 text-sm">
                        <Circle className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-500">Tires:</span>
                        <span className="font-medium text-hul-navy">{tradeIn.tireType}</span>
                      </div>
                    )}
                    {tradeIn.power && (
                      <div className="flex items-center gap-3 text-sm">
                        <Zap className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-500">Power:</span>
                        <span className="font-medium text-hul-navy">{tradeIn.power}</span>
                      </div>
                    )}
                    {tradeIn.workHeight && (
                      <div className="flex items-center gap-3 text-sm">
                        <ArrowUp className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-500">Work Height:</span>
                        <span className="font-medium text-hul-navy">{tradeIn.workHeight}</span>
                      </div>
                    )}
                    {tradeIn.batteryType && (
                      <div className="flex items-center gap-3 text-sm">
                        <Battery className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-500">Battery:</span>
                        <span className="font-medium text-hul-navy">{tradeIn.batteryType}</span>
                      </div>
                    )}
                  </div>

                  {/* Trade-In Value */}
                  <div className="p-4 bg-green-50 rounded-lg border border-green-100">
                    <div className="flex items-center justify-between">
                      <span className="text-green-700 font-medium">Trade-In Value</span>
                      <span className="font-mono text-2xl font-bold text-green-700">
                        {formatCurrency(tradeIn.value)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Trade-In Info */}
      <motion.div
        className="mt-12 p-6 bg-white rounded-xl shadow-lg"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
      >
        <div className="flex flex-wrap items-start gap-8">
          <div className="flex-1 min-w-[200px]">
            <h4 className="font-heading text-lg text-hul-navy mb-3">
              Trade-In Process
            </h4>
            <ul className="space-y-2 text-sm text-gray-600">
              {[
                'Values based on current market conditions',
                'Final inspection at delivery',
                'Credit applied to invoice',
                'We can arrange pickup logistics',
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
          <div className="w-px h-24 bg-gray-200 hidden md:block" />
          <div className="flex-1 min-w-[200px]">
            <h4 className="font-heading text-lg text-hul-navy mb-3">
              Condition Assessment
            </h4>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 bg-green-500 rounded-full" />
                <span className="text-gray-600"><strong>Excellent:</strong> Like new, minimal wear</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 bg-blue-500 rounded-full" />
                <span className="text-gray-600"><strong>Good:</strong> Normal wear, fully operational</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 bg-amber-500 rounded-full" />
                <span className="text-gray-600"><strong>Fair:</strong> Above-average wear, works</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 bg-red-500 rounded-full" />
                <span className="text-gray-600"><strong>Poor:</strong> Needs repairs, high hours</span>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </Section>
  );
}
